import io from "socket.io-client";
import { API_BASE_URL } from "../../config";
import { fetchCasinoGames, upsertCasinoGame, upsertPendingBet, removePendingBet, fetchCasinoPlayers, fetchUserPendingCasinoBets } from "./casinoSlice";
import { patchAppUser } from "./appAuthSlice";

let socket = null;
let initialized = false;

export const getCasinoSocket = () => socket;

const getAppToken = () => {
  try {
    return localStorage.getItem("betbond_app_token") || null;
  } catch {
    return null;
  }
};

// Money-moving events must carry the user's JWT — the server no longer
// trusts client-supplied creatorId/bettorId.
const withAuthToken = (payload = {}) => {
  const token = getAppToken();
  return token ? { ...payload, token } : payload;
};

const emitWithAck = (event, payload, timeoutMs = 45000) =>
  new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error(`Not connected to server (${API_BASE_URL})`));
      return;
    }
    const timer = setTimeout(() => reject(new Error("Request timed out. Please try again.")), timeoutMs);
    try {
      socket.emit(event, withAuthToken(payload), (res) => {
        clearTimeout(timer);
        if (!res) {
          reject(new Error("No response from server"));
          return;
        }
        if (res.ok === false) {
          const err = new Error(res.error || "Bond Spin request failed");
          err.code = res.code;
          err.serverResponse = res;
          reject(err);
          return;
        }
        resolve(res);
      });
    } catch (e) {
      clearTimeout(timer);
      reject(e);
    }
  });

const adjustCoinBalance = (dispatch, getState, delta) => {
  const current = Number(getState?.()?.appAuth?.user?.COIN || 0);
  if (!Number.isFinite(current)) return;
  dispatch(patchAppUser({ COIN: Math.max(0, current + delta) }));
};

export const emitCreateCasinoGame = (payload) => emitWithAck("createCasinoGame", payload);
export const emitJoinCasinoGame = (payload) => emitWithAck("joinCasinoGame", payload);
export const emitCancelCasinoBet = (payload) => emitWithAck("cancelCasinoBet", payload);
export const emitResolveCasinoGame = (payload) => emitWithAck("resolveCasinoGame", payload);

export const runCasinoCreate = async (payload, { dispatch, getState, rejectWithValue } = {}) => {
  try {
    const res = await emitCreateCasinoGame(payload);
    if (res?.casinoGame && dispatch) {
      dispatch(upsertCasinoGame(res.casinoGame));
      adjustCoinBalance(dispatch, getState, -Number(payload.allocatedAmount || 0));
    }
    return res;
  } catch (e) {
    const message = e?.message || "Could not create Bond Spin table.";
    if (rejectWithValue) {
      return rejectWithValue({ message, code: e?.code, error: message, ...(e?.serverResponse || {}) });
    }
    throw e;
  }
};

export const runCasinoJoin = async (payload, { dispatch, getState, rejectWithValue } = {}) => {
  const maxAttempts = 3;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const res = await emitJoinCasinoGame(payload);
      if (res?.bet && dispatch) {
        dispatch(upsertPendingBet(res.bet));
        const game = res.bet.game;
        if (game && typeof game === "object") {
          dispatch(upsertCasinoGame(game));
        }
        adjustCoinBalance(dispatch, getState, -Number(payload.betAmount || 0));
        dispatch(fetchCasinoGames({ limit: 40 }));
      }
      return res;
    } catch (e) {
      const retryable = e?.code === "WRITE_CONFLICT" && attempt < maxAttempts - 1;
      if (retryable) {
        await new Promise((resolve) => setTimeout(resolve, 180 * (attempt + 1)));
        continue;
      }

      const message = e?.message || "Could not reverse ticket.";
      if (rejectWithValue) {
        return rejectWithValue({ message, code: e?.code, error: message, ...(e?.serverResponse || {}) });
      }
      throw e;
    }
  }

  const message = "The table is busy — please try again in a moment.";
  if (rejectWithValue) {
    return rejectWithValue({ message, code: "WRITE_CONFLICT", error: message });
  }
  throw new Error(message);
};

export const runCasinoCancel = async (payload, { dispatch, rejectWithValue } = {}) => {
  try {
    const res = await emitCancelCasinoBet(payload);
    if (dispatch) {
      dispatch(fetchCasinoGames({ limit: 40 }));
    }
    return res;
  } catch (e) {
    const message = e?.message || "Could not cancel bet.";
    if (rejectWithValue) return rejectWithValue({ message });
    throw e;
  }
};

const netCasinoWinCredit = (bet) => {
  const gross = Number(bet?.bettorWinning || 0);
  const stake = Number(bet?.betAmount || 0);
  const profit = Math.max(0, gross - stake);
  return gross - profit * 0.05;
};

export const runCasinoResolve = async (payload, { dispatch, getState, rejectWithValue } = {}) => {
  try {
    const res = await emitResolveCasinoGame(payload);
    if (dispatch && res?.bet) {
      dispatch(removePendingBet(res.bet._id));
      if (res.bet.status === "Won") {
        adjustCoinBalance(dispatch, getState, netCasinoWinCredit(res.bet));
      }
      const game = res.bet.game;
      if (game && typeof game === "object") {
        dispatch(upsertCasinoGame(game));
      }
      dispatch(fetchCasinoGames({ limit: 40 }));
    }
    dispatch(fetchCasinoPlayers());
    return res;
  } catch (e) {
    const message = e?.message || "Could not resolve spin.";
    if (rejectWithValue) return rejectWithValue({ message });
    throw e;
  }
};

export const initCasinoSocket = (dispatch, getState) => {
  if (initialized && socket?.connected) return socket;
  initialized = true;

  if (!socket) {
    socket = io(API_BASE_URL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 30000,
      upgrade: false,
      forceNew: false,
      withCredentials: false,
      auth: (cb) => cb(getAppToken() ? { token: getAppToken() } : {}),
    });
  }

  socket.off("connect");
  socket.off("newCasinoGameCreated");
  socket.off("casinoGameCreated");
  socket.off("casinoGameCompleted");
  socket.off("casinoGameCompletedSuccess");
  socket.off("casinoGameResolved");
  socket.off("casinoGameResolvedSuccess");
  socket.off("casinoGameJoined");
  socket.off("casinoGameJoinedSuccess");
  socket.off("casinoBetCancelledSuccess");
  socket.off("casinoBetRefunded");
  socket.off("casinoGamePoolUpdated");

  socket.on("connect", () => {
    dispatch(fetchCasinoGames({ limit: 40 }));
  });

  socket.on("newCasinoGameCreated", (game) => {
    if (game) dispatch(upsertCasinoGame(game));
  });

  socket.on("casinoGameCreated", ({ casinoGame }) => {
    if (casinoGame) dispatch(upsertCasinoGame(casinoGame));
  });

  socket.on("casinoGamePoolUpdated", (data) => {
    if (data?.casinoGame) {
      dispatch(upsertCasinoGame(data.casinoGame));
      return;
    }
    if (data?.gameId) dispatch(fetchCasinoGames({ limit: 40 }));
  });

  const refreshOrUpsert = (data) => {
    if (data?.casinoGame) {
      dispatch(upsertCasinoGame(data.casinoGame));
      return;
    }
    if (data?.game) {
      dispatch(upsertCasinoGame(data.game));
      return;
    }
    dispatch(fetchCasinoGames({ limit: 40 }));
  };

  socket.on("casinoGameCompleted", refreshOrUpsert);
  socket.on("casinoGameCompletedSuccess", refreshOrUpsert);
  socket.on("casinoGameResolved", refreshOrUpsert);

  socket.on("casinoGameResolvedSuccess", (response) => {
    if (response?.bet?._id) {
      dispatch(removePendingBet(response.bet._id));
    }
    refreshOrUpsert(response);
    dispatch(fetchCasinoPlayers());
  });

  socket.on("casinoGameJoined", ({ bet, gameId }) => {
    if (bet) dispatch(upsertPendingBet(bet));
    if (gameId) dispatch(fetchCasinoGames({ limit: 40 }));
  });

  socket.on("casinoGameJoinedSuccess", ({ bet }) => {
    if (bet) dispatch(upsertPendingBet(bet));
    dispatch(fetchCasinoPlayers());
  });

  socket.on("casinoBetCancelledSuccess", ({ betId, refundedAmount }) => {
    if (betId) dispatch(removePendingBet(betId));
    if (refundedAmount != null && getState) {
      adjustCoinBalance(dispatch, getState, Number(refundedAmount));
    }
    dispatch(fetchCasinoGames({ limit: 40 }));
    dispatch(fetchCasinoPlayers());
  });

  socket.on("casinoBetRefunded", ({ betId, refundedAmount, bettorId }) => {
    const currentUserId = getState?.()?.appAuth?.user?._id || getState?.()?.appAuth?.user?.id;
    if (betId) dispatch(removePendingBet(betId));
    if (
      refundedAmount != null &&
      getState &&
      currentUserId &&
      bettorId &&
      String(bettorId) === String(currentUserId)
    ) {
      adjustCoinBalance(dispatch, getState, Number(refundedAmount));
    }
    dispatch(fetchCasinoGames({ limit: 40 }));
    dispatch(fetchCasinoPlayers());
    if (currentUserId && String(bettorId) === String(currentUserId)) {
      dispatch(fetchUserPendingCasinoBets(currentUserId));
    }
  });

  if (!socket.connected) socket.connect();
  return socket;
};
